import React from 'react'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Typography, Link, Divider } from '@mui/material'
import {
  contentColumnMaxWidthSx,
  contentColumnPageShellSx
} from '../UI/contentColumn'

const documentSx = {
  bgcolor: '#0B0F19',
  minHeight: 'calc(100svh - 80px)',
  height: 'calc(100svh - 80px)',
  borderRadius: 2,
  p: 3,
  overflowX: 'hidden',
  overflowY: 'auto',
  width: '100%',
  boxSizing: 'border-box',
  maxWidth: '100%',
  color: '#E6E9EF',
  display: 'flex',
  flexDirection: 'column',
  gap: 3
}

const bodyTextSx = {
  lineHeight: 1.7,
  color: 'rgba(230,233,239,0.9)'
}

const listSx = {
  pl: 3,
  my: 0,
  color: 'rgba(230,233,239,0.9)'
}

const renderBlock = (block, index) => {
  if (block.type === 'list') {
    return (
      <Box component="ul" sx={listSx} key={index}>
        {block.items.map(item => (
          <Box component="li" key={item} sx={{ mb: 0.9, lineHeight: 1.7 }}>
            {item}
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Typography key={index} variant="body1" sx={bodyTextSx}>
      {block.text}
    </Typography>
  )
}

const LegalPageLayout = ({
  title,
  effectiveDate,
  intro,
  sections,
  contactEmail,
  companyName
}) => {
  return (
    <Box sx={contentColumnPageShellSx}>
      <Box sx={{ ...contentColumnMaxWidthSx, overflowX: 'hidden' }}>
        <Box sx={documentSx}>
          <Box>
            <Typography variant="overline" sx={{ color: '#60A5FA', letterSpacing: 1.4 }}>
              Anear Legal
            </Typography>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#E6E9EF', mt: 0.5, mb: 1.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(230,233,239,0.72)', mb: 2 }}>
              Effective date: {effectiveDate}
            </Typography>
            <Typography variant="body1" sx={bodyTextSx}>
              {intro}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

          {sections.map(section => (
            <Box key={section.title}>
              <Typography variant="h6" sx={{ color: '#E6E9EF', fontWeight: 700, mb: 1.25 }}>
                {section.title}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {section.blocks.map(renderBlock)}
              </Box>
            </Box>
          ))}

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

          <Box>
            <Typography variant="h6" sx={{ color: '#E6E9EF', fontWeight: 700, mb: 1.25 }}>
              Contact
            </Typography>
            <Typography variant="body1" sx={bodyTextSx}>
              {companyName}
            </Typography>
            <Link
              href={`mailto:${contactEmail}`}
              sx={{
                display: 'inline-block',
                mt: 1,
                color: '#60A5FA',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#93C5FD'
                }
              }}
            >
              {contactEmail}
            </Link>
          </Box>

          <Box sx={{ pt: 0.5 }}>
            <Link
              component={RouterLink}
              to="/about"
              underline="hover"
              sx={{ color: '#93C5FD', fontWeight: 500 }}
            >
              Learn more about Anear
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

LegalPageLayout.propTypes = {
  title: PropTypes.string.isRequired,
  effectiveDate: PropTypes.string.isRequired,
  intro: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      blocks: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.oneOf(['paragraph', 'list']).isRequired,
          text: PropTypes.string,
          items: PropTypes.arrayOf(PropTypes.string)
        })
      ).isRequired
    })
  ).isRequired,
  contactEmail: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired
}

export default LegalPageLayout
